import { useEffect, useMemo, useRef, useState } from "react";

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = MS_PER_SECOND * 60;
const MS_PER_HOUR = MS_PER_MINUTE * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;

const toTimestamp = (target) => {
  if (!target) return null;
  const date = target instanceof Date ? target : new Date(target);
  const time = date.getTime();
  return Number.isNaN(time) ? null : time;
};

const split = (remaining) => ({
  days: Math.floor(remaining / MS_PER_DAY),
  hours: Math.floor((remaining % MS_PER_DAY) / MS_PER_HOUR),
  minutes: Math.floor((remaining % MS_PER_HOUR) / MS_PER_MINUTE),
  seconds: Math.floor((remaining % MS_PER_MINUTE) / MS_PER_SECOND),
});

export default function useCountdown(target) {
  const targetTime = useMemo(() => toTimestamp(target), [target]);
  const [now, setNow] = useState(() => Date.now());
  const timerRef = useRef(null);

  useEffect(() => {
    if (targetTime === null) return undefined;

    setNow(Date.now());
    timerRef.current = window.setInterval(() => setNow(Date.now()), MS_PER_SECOND);

    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [targetTime]);

  return useMemo(() => {
    if (targetTime === null) {
      return { available: false, finished: false, days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    const remaining = Math.max(targetTime - now, 0);

    return { available: true, finished: remaining === 0, ...split(remaining) };
  }, [targetTime, now]);
}
